import { useState, type ReactNode } from 'react';

// 危険な削除の共通ルール（社長指示）。
// GitHub の「リポジトリ名を打ち込ませて削除」と同じ type-to-confirm 方式。
// - 対象名（targetLabel）を完全一致でタイプするまで実行ボタンは押せない。
// - 説明文の危険箇所は赤文字で示し、取り消せない操作であることを明示する。
// 破壊的削除はすべてこのダイアログを通すこと（confirm() で済ませない）。
export function DangerDeleteDialog({
  title,
  targetLabel,
  message,
  confirmLabel = '削除する',
  busy = false,
  error,
  onConfirm,
  onClose,
}: {
  title: string;
  // ユーザーに打ち込ませる確認テキスト（＝削除対象の名前）。
  targetLabel: string;
  // 何が起きるかの説明。危険箇所は呼び出し側で <Danger> で囲んで赤文字にする。
  message: ReactNode;
  confirmLabel?: string;
  busy?: boolean;
  error?: Error | null;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const matched = typed.trim() === targetLabel;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 60,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: 480, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0, color: '#b91c1c' }}>⚠️ {title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>

        <div style={{ fontSize: 13.5, lineHeight: 1.7, marginBottom: 14 }}>{message}</div>

        <p style={{ margin: '0 0 6px', fontSize: 13 }}>
          確認のため <code style={{ color: '#b91c1c', fontWeight: 700 }}>{targetLabel}</code> と入力してください。
        </p>
        <input
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={targetLabel}
          autoFocus
          style={{ width: '100%', padding: '8px 12px', fontSize: 14 }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matched && !busy) onConfirm();
          }}
        />

        {error && <p className="k-error" style={{ marginTop: 10 }}>{error.message}</p>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button
            className="btn-danger"
            type="button"
            disabled={!matched || busy}
            onClick={onConfirm}
          >
            {busy ? '削除中…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// 説明文中の危険箇所を赤文字にする小物。<Danger>この操作は取り消せません</Danger> のように使う。
export function Danger({ children }: { children: ReactNode }) {
  return <strong style={{ color: '#dc2626' }}>{children}</strong>;
}
